import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

export default function Register() {
  const [formData, setFormData] = useState({ name: '', email: '', otp: '' });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clock, setClock] = useState(new Date());

  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const API_BASE_URL = 'https://api.avtechbmt.cloud/api/v1/auth';

  /* Live clock for the editorial panel */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // 1. Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');

      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 2. Verify OTP with Name & Auto Login
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp,
          name: formData.name
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Verification failed');

      loginWithToken(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 3. Direct Google Authentication & Auto Login
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const decodedToken = JSON.parse(atob(credentialResponse.credential.split('.')[1]));

      const res = await fetch(`${API_BASE_URL}/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          googleId: decodedToken.sub,
          name: decodedToken.name,
          email: decodedToken.email
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Google signup failed');

      loginWithToken(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Register - AV Tech Baramati</title>
        <meta name="description" content="Create your AV Tech account using OTP or Google." />
        <link rel="canonical" href="https://avtechbmt.cloud/register" />
      </Helmet>

      <div className="av-auth-page">
        <div className="av-auth-grain" aria-hidden="true" />

        {/* ============ LEFT — AUTH CARD ============ */}
        <main className="av-auth-left">
          <div className="av-auth-card">
            <span className="av-auth-tag">02 / Identity</span>
            <h2 className="av-auth-title">
              Create <em>Account</em>
            </h2>
            <p className="av-auth-subtitle">
              {step === 1
                ? 'Enter your details to generate an authorization code.'
                : `Passcode sent to ${formData.email}`}
            </p>

            {error && <div className="av-auth-error">{error}</div>}

            {step === 1 ? (
              <>
                <form onSubmit={handleSendOtp} className="av-auth-form">
                  <div className="av-auth-form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      required
                      placeholder="Jane Doe"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="av-auth-form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      required
                      placeholder="engineer@domain.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="av-auth-submit"
                  >
                    {loading ? 'Sending Code…' : 'Request Passcode →'}
                  </button>
                </form>

                <div className="av-auth-divider">
                  <span>OR</span>
                </div>

                <div className="av-auth-google-wrapper">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google Authentication Failed')}
                    text="signup_with"
                    shape="rectangular"
                    theme="outline"
                  />
                </div>
              </>
            ) : (
              <form onSubmit={handleVerifyOtp} className="av-auth-form">
                <div className="av-auth-form-group">
                  <label>Enter 6-Digit OTP</label>
                  <input
                    type="text"
                    required
                    maxLength="6"
                    placeholder="123456"
                    value={formData.otp}
                    onChange={(e) =>
                      setFormData({ ...formData, otp: e.target.value })
                    }
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="av-auth-submit"
                >
                  {loading ? 'Verifying Account…' : 'Complete Registration →'}
                </button>
                <button
                  type="button"
                  className="av-auth-resend"
                  onClick={() => setStep(1)}
                >
                  ← Edit Details
                </button>
              </form>
            )}

            <p className="av-auth-footer">
              Already registered? <Link to="/login">Sign in here</Link>
            </p>
          </div>
        </main>

        {/* ============ RIGHT — EDITORIAL PANEL ============ */}
        <aside className="av-auth-right">
          <div className="av-auth-giant-type" aria-hidden="true">
            <span>JOIN</span>
          </div>

          <div className="av-auth-right-top">
            <div className="av-auth-right-badge">
              <span className="av-auth-right-badge-dot" />
              <span className="av-auth-tag">02 / Identity</span>
            </div>

            <h1 className="av-auth-right-title">
              Create your
              <br />
              <em>Account.</em>
            </h1>

            <p className="av-auth-right-sub">
              Join AV Tech to unlock client dashboards, project tracking, and
              priority support. Register in seconds with OTP or Google.
            </p>

            <ul className="av-auth-right-features">
              <li className="av-auth-right-feature">
                <span className="av-auth-right-feature-check">✓</span>
                <span>Instant access to your workspace</span>
              </li>
              <li className="av-auth-right-feature">
                <span className="av-auth-right-feature-check">✓</span>
                <span>Passwordless OTP & Google SSO onboarding</span>
              </li>
              <li className="av-auth-right-feature">
                <span className="av-auth-right-feature-check">✓</span>
                <span>Secure, encrypted, GDPR-friendly</span>
              </li>
            </ul>
          </div>

          <div className="av-auth-right-bottom">
            <div className="av-auth-right-meta-item">
              <span className="av-auth-right-meta-label">Local Time</span>
              <span className="av-auth-right-meta-value">
                {clock.toLocaleTimeString('en-US', { hour12: false })}
              </span>
            </div>
            <div className="av-auth-right-meta-item">
              <span className="av-auth-right-meta-label">Status</span>
              <span className="av-auth-right-meta-value av-auth-right-meta-value--live">
                <span className="av-auth-live-dot" /> Open
              </span>
            </div>
            <div className="av-auth-right-meta-item">
              <span className="av-auth-right-meta-label">Location</span>
              <span className="av-auth-right-meta-value">Baramati, IN</span>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}